import { useEffect, useState } from "react";
import StatusPill from "shared/StatusPill";
import { getPathActivity, type PathActivity, type PathActivityItem } from "../api/OutreachTouchesApi";
import { KIND_LABELS, formatCalendarDate, formatKarachi } from "../outreachDates";

export function useActivity(opportunityId: number, companyId: number) {
  const [data, setData] = useState<PathActivity | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    getPathActivity(opportunityId, companyId).then((d) => alive && setData(d)).catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [opportunityId, companyId]);
  return { data, failed };
}

function Entry({ item }: { item: PathActivityItem }) {
  const system = item.kind === "system";
  return (
    <li className="relative pl-6 pb-5 last:pb-0" data-testid="activity-entry" data-kind={item.kind} data-undone={item.undone}>
      <span aria-hidden className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ${system ? "bg-border" : "bg-primary"}`} />
      <span aria-hidden className="absolute left-[4px] top-4 bottom-0 w-px bg-border" />
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <p className={`text-sm ${system ? "text-muted-foreground" : "text-foreground font-medium"} ${item.undone ? "line-through" : ""}`}>{item.text}</p>
        {item.undone && <StatusPill tone="neutral" size="sm">undone</StatusPill>}
        {item.back_dated && item.on && <StatusPill tone="warning" size="sm">back-dated to {formatCalendarDate(item.on, false)}</StatusPill>}
      </div>
      <p className="text-xs text-muted-foreground">
        {system ? "Automatic" : item.actor_label}
        {!system && item.touch_kinds.length > 0 && <> · {item.touch_kinds.map((k) => KIND_LABELS[k] ?? k).join(", ")}</>}
        {item.at && <> · {formatKarachi(item.at)}</>}
      </p>
      {item.note && <p className="mt-1 rounded-md bg-surface-muted px-2.5 py-1.5 text-xs text-foreground">{item.note}</p>}
      {item.effects.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{item.effects.join(" · ")}</p>}
    </li>
  );
}

/** Who did what, and when, on one path: newest first, undone actions kept and marked, the app's own steps shown muted. */
export default function PathActivityFeed({ opportunityId, companyId }: { opportunityId: number; companyId: number }) {
  const { data, failed } = useActivity(opportunityId, companyId);
  const [showSystem, setShowSystem] = useState(true);
  if (failed) return <p className="text-sm text-muted-foreground">Could not load the activity.</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Loading the activity…</p>;
  if (data.items.length === 0) return <p className="text-sm text-muted-foreground">Nothing has been recorded on this path yet.</p>;
  const items = showSystem ? data.items : data.items.filter((i) => i.kind === "person");
  const people = data.items.filter((i) => i.kind === "person").length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{people} {people === 1 ? "action" : "actions"} by people · {data.count - people} automatic</span>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={showSystem} onChange={(e) => setShowSystem(e.target.checked)} />
          Show automatic steps
        </label>
      </div>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No action by a person yet.</p> : <ol aria-label="Activity" className="m-0 list-none p-0">{items.map((i) => <Entry key={i.group} item={i} />)}</ol>}
    </div>
  );
}
